import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "hello.ateliernoir@gmail.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — I'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email me directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 py-4 text-lg text-white placeholder-white/30 focus:border-[#d4af37] transition-colors duration-300";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-[#d4af37]" />
              <span className="text-xs uppercase tracking-[0.3em] gold-text">Get In Touch</span>
            </div>
            <h2 className="font-serif-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
              Let&apos;s build something <span className="italic gold-text">premium.</span>
            </h2>
            <p className="mt-8 text-white/50 leading-relaxed max-w-md text-lg">
              Have a project in mind? Tell me about it and I&apos;ll get back to you with how we
              can make it happen.
            </p>

            <div className="mt-12">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">Email me at</p>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="hover-line inline-flex items-center gap-2 font-serif-display text-2xl md:text-3xl text-white hover:text-[#d4af37] transition-colors duration-300 break-all"
              >
                {EMAIL}
                <ArrowUpRight size={22} className="shrink-0" />
              </a>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="glass-card rounded-3xl p-8 md:p-12 flex flex-col gap-8"
          >
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Name</label>
              <input
                data-testid="contact-name-input"
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Email</label>
              <input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@company.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Subject</label>
              <input
                data-testid="contact-subject-input"
                value={form.subject}
                onChange={update("subject")}
                placeholder="What's it about?"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Message</label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={update("message")}
                placeholder="Tell me about your project..."
                rows={4}
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={loading}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#d4af37] text-black font-medium tracking-wide hover:bg-[#f3e5ab] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Send Message
                  <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
